import { makeAutoObservable, runInAction } from 'mobx';
import {
  InputType,
  StepType,
  authSectionTypes,
  formsType,
  sectionTypes,
} from './authorization.type';
import { apiServiceV2 } from '../../services/apiService';
import { cacheService } from '../../services/cacheService';
import { globalStore } from '../../global.store';
import { editAvatarStore } from '../../components/editAvatar/editAvatar.store';
import { authErrors, AuthForm } from '../../helpers/authorization_form';
import { formatDate } from '../../helpers/utils';
import { CustomAxiosResponse } from '../../types/global.types';

export class Authorization {
  redirectUrl = '';

  userData: any = {};

  clearSearchParams = false;

  steps: { [key in sectionTypes]: number } = {
    login: 1,
    registration: 1,
    forgot: 1,
  };

  typeofSection: sectionTypes = 'login';

  forms: formsType = {
    login: {},
    registration: {},
    forgot: {},
  };

  authSections: authSectionTypes = AuthForm;

  sectionContent: StepType;

  openUnverifiedPopup = false;

  showTerms = '';

  constructor() {
    makeAutoObservable(this);
  }

  setRedirectUrl = (url: string) => {
    this.redirectUrl = url;
  };

  setClearSearchParams = (bool: boolean) => {
    this.clearSearchParams = bool;
  };

  setOpenUnverifiedPopup = (state: boolean) => {
    this.openUnverifiedPopup = state;
  };

  setShowTerms = (str: string) => {
    this.showTerms = str;
  };

  setButtonDisabledStatus = (status: boolean) => {
    this.currentSection.button.disabled = status;
  };

  setErrorStatus = (status: boolean) => {
    this.currentSection.error.status = status;
  };

  setErrorMessage = (message: string) => {
    this.currentSection.error.message = message;
  };

  setSectionContent = (
    type: sectionTypes,
    resetSection: boolean = true,
  ): void => {
    if (resetSection) this.resetSection();
    this.typeofSection = type;
    this.sectionContent =
      this.authSections[type][this.steps[this.typeofSection]];
  };

  continue = (): void => {
    this.steps[this.typeofSection] += 1;
    this.setSectionContent(this.typeofSection);
  };

  goToVerification = (): void => {
    this.steps.login = 1;
    this.steps.registration = 2;
    this.setSectionContent('registration');
  };

  back = (type: number): void => {
    this.steps[this.typeofSection] -= type;
    this.setSectionContent(this.typeofSection, false);
  };

  loginWallet = async (token: string) => {
    cacheService.clear();
    cacheService.set('auth_token', token);
    globalStore.setIsLogin(true);
    await globalStore.projectConfiguration();
    this.setClearSearchParams(true);
  };

  logIn = async (data: any, fromReg = false): Promise<boolean | string> => {
    const loginData = data
      ? {
          ...data,
        }
      : {
          ...this.forms.login,
        };

        const response: CustomAxiosResponse = await apiServiceV2.login(loginData);
    if (response.error) {
      runInAction(() => {
        if (response.data?.is_email_confirmed) {
          const pass = data?.password || this.forms.login.password;
          this.setOpenUnverifiedPopup(true);
          this.forms.registration.email = response.data.email;
          this.forms.registration.userId = response.data.id;
          this.forms.registration.password = pass;
        } else {
          this.setErrorStatus(true);
          this.setErrorMessage(
            'Your email or password is incorrect. Please check and try again',
          );
          this.setButtonDisabledStatus(true);
        }
      });
      return false;
    }
    cacheService.clear();
    cacheService.set('auth_token', response.data.accessToken.token);
    globalStore.setIsLogin(true);
    this.steps = {
      login: 1,
      registration: 1,
      forgot: 1,
    };
    await globalStore.projectConfiguration();
    if (fromReg) {
      editAvatarStore.setShowEditAvatarPopup(true);
      this.setClearSearchParams(true);
    }
    return 'delete';
  };

  registration = async (): Promise<boolean | void> => {
    const registerForm = {
      username: this.forms.registration.username,
      email: this.forms.registration.email,
      password: this.forms.registration.password,
      gender: Number(this.forms.registration.gender),
      birthday: formatDate(this.forms.registration.date as string | Date),
    };
    const response: CustomAxiosResponse =
      await apiServiceV2.registration(registerForm);
    if (response.error as boolean) {
      runInAction(() => {
        if (response?.reasons?.messages) {
          Object.keys(response?.reasons?.messages).forEach((reason: any) => {            
            this.setLoginForm(
              reason,
              this.forms.registration[reason] as string,
              false,
              authErrors[reason] || response?.reasons?.messages[reason],
            );
            const getLabel = this.currentSection.inputs.filter(
              (el: any) =>
                (el.name === 'name' && reason === 'username') ||
                el.name === reason,
            )[0];
            if (getLabel) {
              getLabel.validation.error = authErrors[reason] || response?.reasons?.messages[reason]
              getLabel.validation.status = false;
            }
          });
        } else {
          this.setErrorStatus(response.error);
          this.setErrorMessage(response?.reasons?.messages as string);
          this.setButtonDisabledStatus(true);
        }
      });
    } else {
      this.forms.registration.userId = response.data.publicId;
      this.authSections.registration[2]!.subtitle!.navigation!.text = this.forms
        .registration.email as string;
      this.continue();
    }
    return false;
  };

  verifyEmail = async (): Promise<string | void> => {
    const response: CustomAxiosResponse =
      await apiServiceV2.setRegistrationVerifyCode(
        this.forms.registration.userId as string,
        this.forms.registration.code as string,
      );
    if (response.error) {
      runInAction(() => {
        this.setLoginForm(
          'code',
          this.forms.registration.code as string,
          false,
        );
        const getLabel = this.currentSection.inputs.filter(
          (el: any) => el.name === 'code',
        )[0];
        if (getLabel) {
          getLabel.validation.error = 'Incorrect code!';
          getLabel.validation.status = false;
        }
        this.setButtonDisabledStatus(true);
      });
    } else {
      return (async () => {
        await this.logIn(
          {
            email: this.forms.registration.email,
            password: this.forms.registration.password,
          },
          true,
        );
      })();
    }
  };

  checkForgotEmail = async (): Promise<void> => {
    const response: CustomAxiosResponse = await apiServiceV2.setForgotEmail({
      email: this.forms.forgot.email as string,
    });
    if (response.error) {
      runInAction(() => {
        this.setErrorStatus(true);
        this.setErrorMessage('The email is not registered');
        this.setButtonDisabledStatus(true);
      });
    } else {
      this.authSections.forgot[2]!.subtitle!.navigation!.text = this.forms
        .forgot.email as string;
      this.continue();
    }
  };

  resendForgotCode = (): void => {
    apiServiceV2.setForgotEmail({
      email: this.forms.forgot.email as string,
    });
  };

  resendVerifyCode = (): void => {
    apiServiceV2.setForgotEmail({
      email: this.forms.registration.email as string,
    });
  };

  checkForgotCode = async (): Promise<void> => {
    const response: CustomAxiosResponse = await apiServiceV2.setForgotCode({
      email: this.forms.forgot.email as string,
      code: this.forms.forgot.code as string,
    });
    if (response.error) {
      runInAction(() => {
        this.setErrorStatus(true);
        this.setErrorMessage(response?.message);
        this.setButtonDisabledStatus(true);
      });
    } else {
      this.continue();
    }
  };

  createNewPass = async (): Promise<boolean | string | void> => {
    const response: CustomAxiosResponse = await apiServiceV2.setNewPassword({
      email: this.forms.forgot.email as string,
      newPasswordOne: this.forms.forgot.password as string,
      newPasswordTwo: this.forms.forgot.confpassword as string,
      code: this.forms.forgot.code,
    });
    if (response.error) {
      runInAction(() => {
        this.setErrorStatus(true);
        this.setErrorMessage(response?.errors?.detail);
        this.setButtonDisabledStatus(true);
      });
      return false;
    }
    return (async () => {
      await this.logIn({
        email: this.forms.forgot.email,
        password: this.forms.forgot.password,
      });
    })();
  };

  setLoginForm = (
    name: string,
    value: string,
    valid: boolean,
    errorMessage = '',
  ) => {
    this.forms[this.typeofSection][name] = value;
    this.currentSection.inputs = this.currentSection.inputs.map(
      (input: InputType) => {
        if (input.name === name) {
          input.value = value;
          if (input.type === 'select') {
            input.value = input.value === '1' ? 'Male' : 'Female';
          }
          input.validation.status = valid;
          input.validation.error = errorMessage;
        }
        return input;
      },
    );
    this.setButtonDisabledStatus(false);
    this.authSections[this.typeofSection][
      this.steps[this.typeofSection]
    ].inputs.forEach((input: InputType) => {
      if (!input.validation.status) this.setButtonDisabledStatus(true);
    });
    this.setErrorStatus(false);
  };

  resetSection = (): void => {
    this.setButtonDisabledStatus(true);
    this.currentSection.error = {
      status: false,
      message: '',
    };
    this.currentSection.inputs.forEach((input: InputType) => {
      input.validation.status = null;
      input.value = ''
    });
  };

  resetSteps = () => {
    this.steps = {
      login: 1,
      registration: 1,
      forgot: 1,
    };
  };

  clearAuthData = () => {
    this.userData = {};
  };

  get currentSection(): StepType {
    return this.authSections[this.typeofSection][
      this.steps[this.typeofSection]
    ];
  }
}

export const authorizationStore: Authorization = new Authorization();
